import { useMemo } from 'react'
import { createMemoCell } from '@/lib/create-memo-cell'
import { cn } from '@/lib/utils'
import { formatCurrency, formatPercentage } from '@/lib/format'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Typography } from '@/components/ui/typography'
import type { CellProps } from '../types'

interface CellNumberProps extends CellProps<
  number | string | null | undefined
> {
  /** Locale for formatting */
  locale?: string
  /** Minimum fraction digits */
  minimumFractionDigits?: number
  /** Maximum fraction digits */
  maximumFractionDigits?: number
  /** Display format: 'currency', 'percentage', or undefined for default */
  format?: string
  /** As of date for this value (e.g., from Chronograph LTM period end date) */
  asOfDate?: string | Date | null
}

function CellNumberBase({
  value,
  locale = 'en-US',
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  format,
  asOfDate,
  className,
}: CellNumberProps) {
  const isNegative = useMemo(() => {
    if (value === null || value === undefined || value === '') return false
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    return !isNaN(numValue) && numValue < 0
  }, [value])

  const formattedValue = useMemo(() => {
    // Normalize format to lowercase for comparison (backend sends "CURRENCY", "PERCENTAGE")
    const normalizedFormat = format?.toLowerCase()

    if (normalizedFormat === 'currency') {
      return formatCurrency(value)
    }

    if (normalizedFormat === 'percentage') {
      return formatPercentage(value)
    }

    if (value === null || value === undefined || value === '') {
      return '-'
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) {
      return '-'
    }

    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
      }).format(numValue)
    } catch {
      return String(numValue)
    }
  }, [value, locale, minimumFractionDigits, maximumFractionDigits, format])

  const formattedAsOfDate = useMemo(() => {
    if (!asOfDate) return null

    try {
      const date = typeof asOfDate === 'string' ? new Date(asOfDate) : asOfDate
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date)
    } catch {
      return null
    }
  }, [asOfDate])

  // If there's an as-of date, wrap in popover with triangle indicator
  if (formattedAsOfDate) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <div
            className="relative group -m-2 block cursor-help p-2"
            tabIndex={0}
            role="button"
            aria-haspopup="dialog"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                event.currentTarget.click()
              }
            }}
          >
            {/* Triangle indicator in top-right - uses clip-path for triangle shape */}
            <div
              className="absolute top-1 right-1 w-2 h-2 rounded-[1px] bg-primary/30 group-hover:bg-primary/80 transition-colors cursor-help"
              style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}
              role="img"
              aria-label="Data as-of date indicator"
            />
            <span
              className={cn(
                'tabular-nums text-left block',
                formattedValue === '-' && 'text-muted-foreground',
                isNegative && 'text-destructive',
                className
              )}
            >
              {formattedValue}
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto px-2.5 py-1 rounded-sm bg-white border border-primary/50 shadow-[0px_3px_6px_0px_rgba(0,0,0,0.1),0px_11px_11px_0px_rgba(0,0,0,0.09),0px_25px_15px_0px_rgba(0,0,0,0.05),0px_45px_18px_0px_rgba(0,0,0,0.01)]"
          side="top"
          align="end"
          alignOffset={-100} // Shift popover left to align with triangle indicator in top-right
          sideOffset={4}
        >
          <Typography
            variant="body-sm"
            className="text-[10px] leading-5 text-primary whitespace-nowrap"
          >
            As of · {formattedAsOfDate}
          </Typography>
        </PopoverContent>
      </Popover>
    )
  }

  // No as-of date, render simple number
  return (
    <span
      className={cn(
        'tabular-nums text-left block',
        formattedValue === '-' && 'text-muted-foreground',
        isNegative && 'text-destructive',
        className
      )}
    >
      {formattedValue}
    </span>
  )
}

export const CellNumber = createMemoCell(CellNumberBase, 'CellNumber')
